import { Controller } from "@hotwired/stimulus"

// Where a merge puts one of the folding-in figure's options (review/_option_map):
// its tile opens the places to pick from, each a radio named for the option;
// picking one shows its tile and closes the list. "Find the figure it is…"
// searches figures (figures#lookup) and adds the one picked as a place,
// "figure:ID", picked.
export default class extends Controller {
  static targets = ["current", "choices", "query", "results"]
  static values = { lookup: String, self: Number, name: String, form: String, beside: String, source: String, option: String }

  // The tile shows the place picked. In the pop-up's copy (rw_peek_controller), a figure found on the card has no
  // place of its own to show, so none is picked there.
  connect() {
    this.outside = event => { if (!this.element.contains(event.target)) this.element.open = false }
    document.addEventListener("click", this.outside)
    const picked = this.element.querySelector("input[type=radio]:checked")
    if (picked) this.pick({ target: picked })
    else this.currentTarget.textContent = "As picked on the card"
  }

  disconnect() {
    document.removeEventListener("click", this.outside)
  }

  // Its tile shown, and the Side by side button after the list shown for another figure (data-beside), else hidden.
  pick(event) {
    const label = event.target.closest("label")
    const tile = label && [...label.children].find(child => child.tagName !== "INPUT")
    if (tile) this.currentTarget.replaceChildren(tile.cloneNode(true))
    this.element.open = false
    const compare = this.element.nextElementSibling
    if (compare?.hasAttribute("data-rw-merge-dest-compare")) {
      const url = event.target.dataset.beside
      compare.hidden = !url
      if (url) compare.href = url
    }
  }

  search() {
    clearTimeout(this.timer)
    const query = this.queryTarget.value.trim()
    if (query.length < 2) { this.resultsTarget.hidden = true; return }
    this.timer = setTimeout(() => this.fetch(query), 200)
  }

  async fetch(query) {
    const url = new URL(this.lookupValue, window.location.origin)
    url.searchParams.set("q", query)
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok || query !== this.queryTarget.value.trim()) return
    const figures = (await response.json()).filter(figure => figure.id !== this.selfValue)
    this.resultsTarget.replaceChildren(...figures.map(figure => this.result(figure)))
    this.resultsTarget.hidden = figures.length === 0
  }

  result(figure) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "cx-result"
    button.setAttribute("role", "option")
    const name = document.createElement("b")
    name.textContent = figure.name
    button.append(name)
    button.addEventListener("click", () => this.add(figure))
    return button
  }

  // The figure found becomes a place of its own, picked.
  add(figure) {
    const input = this.offer(figure)
    input.checked = true
    this.pick({ target: input }) // its data-action isn't wired up until Stimulus sees the new radio
    input.dispatchEvent(new Event("change", { bubbles: true })) // for the lines counting where options go
    this.queryTarget.value = ""
    this.resultsTarget.hidden = true
  }

  // The figure as a place of its own, not picked (the card's "Another figure it goes into", rw-merge-targets): its radio.
  offer(figure) {
    const value = `figure:${figure.id}`
    let input = this.choicesTarget.querySelector(`input[value="${value}"]`)
    if (!input) {
      const label = document.createElement("label")
      label.className = "block cursor-pointer rounded has-[:checked]:ring-2 has-[:checked]:ring-yellow-400 hover:ring-2 hover:ring-gray-500"
      input = Object.assign(document.createElement("input"), { type: "radio", className: "sr-only", name: this.nameValue, value })
      if (this.formValue) input.setAttribute("form", this.formValue)
      input.dataset.action = "change->rw-merge-dest#pick"
      const beside = new URL(this.besideValue, window.location.origin)
      beside.searchParams.set("keys", `${this.sourceValue},figure-${figure.id}`)
      input.dataset.beside = beside.pathname + beside.search
      input.dataset.sum = figure.name
      input.dataset.option = this.optionValue
      label.className += " flex-1 min-w-0"
      label.append(input, this.figureTile(figure))
      const row = document.createElement("span")
      row.className = "flex items-center gap-1.5"
      row.append(label)
      this.queryTarget.closest("span").before(row)
    }
    return input
  }

  // As the card's tiles look (review/_option_map): the figure it goes into, then what it becomes there.
  figureTile(figure) {
    const tile = document.createElement("span")
    tile.className = "flex items-center gap-2 rounded border-2 border-teal-400/80 bg-teal-950/40 px-2 py-1.5 text-left min-w-0"
    const dot = Object.assign(document.createElement("span"), { className: "w-2.5 h-2.5 rounded-full flex-shrink-0 bg-teal-400" })
    dot.setAttribute("aria-hidden", "true")
    const words = Object.assign(document.createElement("span"), { className: "min-w-0 break-words" })
    const into = Object.assign(document.createElement("b"), { className: "text-teal-300", textContent: figure.name })
    const what = Object.assign(document.createElement("span"), { className: "text-gray-400", textContent: ` · as ${this.optionValue}` })
    words.append(into, what)
    tile.append(dot, words)
    return tile
  }
}
